/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";
import { ReimbursementRequest } from "services/GraphQl";

interface ApproveReimbursementModalProps {
  open: boolean;
  onClose: () => void;
  request: ReimbursementRequest | null;
  onConfirm: (requestId: string, note: string) => void;
}

const ApproveReimbursementModal: React.FC<ApproveReimbursementModalProps> = ({
  open,
  onClose,
  request,
  onConfirm,
}) => {
  const [note, setNote] = useState("");
  const handleConfirm = () => {
    if (request) {
      onConfirm(request.reimbursementRequestId, note);
      setNote(""); // Reset note after confirmation
      onClose();
    }
  };

  const handleClose = () => {
    setNote(""); // Reset note when closing
    onClose();
  };

  if (!request) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      {/* Header with close button */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, pb: 1 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.5rem", fontWeight: "bold" }}>
          Approve Reimbursement
        </DialogTitle>
        <IconButton onClick={handleClose} sx={{ color: "text.secondary" }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You are about to approve this reimbursement request.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>Request ID:</strong> {request.reimbursementRequestId}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: "500" }}>
            Add a note (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter your note here..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#d1d5db",
                  borderWidth: 2,
                },
                "&:hover fieldset": {
                  borderColor: "#9ca3af",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3b82f6",
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            borderColor: "#d1d5db",
            color: "#374151",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="outlined"
          startIcon={<CheckCircle />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            borderColor: "#10b981",
            color: "#10b981",
            "&:hover": {
              borderColor: "#059669",
              backgroundColor: "#ecfdf5",
            },
          }}
        >
          Confirm Approval
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveReimbursementModal; 